import { Rect } from '@ritabygglov/core';
import { createDimensionId } from '../../../helpers/dimensionIds';
import { makeResizeCursorUrl } from '../../../helpers/utility';
import { useMeasurement } from '../../../hooks/useMeasurement';
import { getCornerCoords } from '../Elements';
import { DimensionLine } from './components/DimensionLine';

type RectDimensionsProps = {
  element: Rect;
  scalingFactor: number;
  scale: number;
  draggingDimensionId: string | null;
};

const getWidthDimension = (element: Rect) => {
  const distanceY = element.dimensionOffsetWidth ?? 15;

  const { bR, bL } = getCornerCoords(element);
  const lineStart: [number, number] = [bL.x, bL.y + distanceY];
  const lineEnd: [number, number] = [bR.x, bR.y + distanceY];
  const cornerStart: [number, number] = [bL.x, bL.y];
  const cornerEnd: [number, number] = [bR.x, bR.y];

  const x = bL.x + element.width / 2;
  const y = bL.y + distanceY + 20;

  return {
    lineStart,
    lineEnd,
    cornerStart,
    cornerEnd,
    x,
    y,
  };
};

const getHeightDimension = (element: Rect) => {
  const { tL, bL } = getCornerCoords(element);
  const distanceX = element.dimensionOffsetHeight ?? 15;

  const lineStart: [number, number] = [tL.x - distanceX, tL.y];
  const lineEnd: [number, number] = [bL.x - distanceX, bL.y];
  const cornerStart: [number, number] = [tL.x, tL.y];
  const cornerEnd: [number, number] = [bL.x, bL.y];

  const transform = `translate(${tL.x - distanceX - 20},${
    tL.y + element.height / 2
  })rotate(90)`;

  return {
    lineStart,
    lineEnd,
    cornerStart,
    cornerEnd,
    transform,
  };
};

export const RectDimensions = ({
  element,
  scalingFactor,
  scale,
  draggingDimensionId,
}: RectDimensionsProps) => {
  const { formatLength } = useMeasurement();
  const width = element.width * scalingFactor;
  const height = element.height * scalingFactor;
  const fontSize = element.style?.fontSize
    ? parseInt(element.style.fontSize as string, 10)
    : 16;

  const widthDimension = getWidthDimension(element);
  const heightDimension = getHeightDimension(element);

  const widthDimensionId = createDimensionId(element.id, 'width');
  const heightDimensionId = createDimensionId(element.id, 'height');

  const isWidthDragging = draggingDimensionId === widthDimensionId;
  const isHeightDragging = draggingDimensionId === heightDimensionId;

  const widthCursor = makeResizeCursorUrl(element.rotate + 90);
  const heightCursor = makeResizeCursorUrl(element.rotate);

  return (
    <g>
      <DimensionLine
        scale={scale}
        id={widthDimensionId}
        lineStart={widthDimension.lineStart}
        lineEnd={widthDimension.lineEnd}
        cornerStart={widthDimension.cornerStart}
        cornerEnd={widthDimension.cornerEnd}
        isDragging={isWidthDragging}
        style={{ cursor: widthCursor }}
      >
        <text
          className="dimension-text"
          style={{ fontSize }}
          x={widthDimension.x}
          y={widthDimension.y}
        >
          {formatLength(width)}
        </text>
      </DimensionLine>

      <DimensionLine
        scale={scale}
        id={heightDimensionId}
        lineStart={heightDimension.lineStart}
        lineEnd={heightDimension.lineEnd}
        cornerStart={heightDimension.cornerStart}
        cornerEnd={heightDimension.cornerEnd}
        isDragging={isHeightDragging}
        style={{ cursor: heightCursor }}
      >
        <text
          className="dimension-text"
          style={{ fontSize }}
          transform={heightDimension.transform}
        >
          {formatLength(height)}
        </text>
      </DimensionLine>
    </g>
  );
};
